<script lang="ts">
  import { _ } from 'svelte-i18n';
  import type { ContentItem, ContentType, Locale } from '../../content/loader';
  
  interface Props {
    item: ContentItem;
    locale: Locale;
    compact?: boolean;
  }
  
  let { item, locale, compact = false }: Props = $props();

  function getContentTypeUrl(item: ContentItem): string {
    if ('author' in item && 'category' in item) return 'blog';
    if ('version' in item && 'type' in item) return 'changelog';
    if ('publication' in item && 'url' in item) return 'press';
    if ('customer' in item && 'industry' in item) return 'stories';
    return 'blog'; // fallback
  }

  // Generate URL based on content type
  function getContentUrl(item: ContentItem, locale: Locale): string {
    const typeUrl = getContentTypeUrl(item);
    const baseUrl = `/${typeUrl}/${item.slug}`;
    return locale === 'en' ? baseUrl : `/${locale}${baseUrl}`;
  }
  
  function getContentType(item: ContentItem): ContentType {
    if (!item) return 'blog'; // Guard against undefined
    if ('author' in item && 'category' in item) return 'blog';
    if ('version' in item && 'type' in item) return 'changelog';
    if ('publication' in item && 'url' in item) return 'press';
    if ('customer' in item && 'industry' in item) return 'stories';
    return 'blog'; // fallback
  }
  
  function formatDate(dateString: string, locale: Locale): string {
    const date = new Date(dateString);
    const localeCode = locale.split('-')[0]; // Convert 'en-US' to 'en'
    
    return date.toLocaleDateString(localeCode, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  function getTypeLabel(type: ContentType): string {
    switch (type) {
      case 'blog': return $_('content.types.blog');
      case 'changelog': return $_('content.types.changelog');
      case 'press': return $_('content.types.press');
      case 'stories': return $_('content.types.stories');
      default: return '';
    }
  }
  
  function getTypeIcon(type: ContentType): string {
    switch (type) {
      case 'blog': return '📝';
      case 'changelog': return '🚀';
      case 'press': return '📰';
      case 'stories': return '💼';
      default: return '📄';
    }
  }
  
  const contentType = $derived(getContentType(item));
  const isExternal = $derived(contentType === 'press' && item && 'url' in item);
</script>

{#if item}
<article class="content-card {compact ? 'content-card--compact' : ''}">
  <div class="content-card__header">
    <div class="content-card__meta">
      <span class="content-card__type">
        <span class="content-card__icon">{getTypeIcon(contentType)}</span>
        {getTypeLabel(contentType)}
      </span>
      <time class="content-card__date" datetime={item.date}>
        {formatDate(item.date, locale)}
      </time>
    </div>
    
    {#if 'featured' in item && item.featured}
      <span class="content-card__featured">
        ⭐ {$_('content.featured')}
      </span>
    {/if}
  </div>
  
  <div class="content-card__content">
    <h3 class="content-card__title">
      {#if isExternal}
        <a 
          href={item.url} 
          target="_blank" 
          rel="noopener noreferrer"
          class="content-card__link content-card__link--external"
        >
          {item.title}
          <svg class="external-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clip-rule="evenodd" />
            <path fill-rule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clip-rule="evenodd" />
          </svg>
        </a>
      {:else}
        <a href={getContentUrl(item, locale)} class="content-card__link">
          {item.title}
        </a>
      {/if}
    </h3>
    
    <p class="content-card__excerpt">
      {item.excerpt}
    </p>
    
    <div class="content-card__footer">
      <div class="content-card__details">
        {#if contentType === 'blog' && 'author' in item}
          <span class="content-card__author">
            👤 {item.author}
          </span>
        {/if}
        
        {#if contentType === 'changelog' && 'version' in item}
          <span class="content-card__version">
            🏷️ v{item.version}
          </span>
        {/if}
        
        {#if contentType === 'press' && 'publication' in item}
          <span class="content-card__publication">
            📰 {item.publication}
          </span>
        {/if}
        
        {#if contentType === 'stories' && 'customer' in item}
          <span class="content-card__customer">
            🏢 {item.customer}
          </span>
        {/if}
        
        {#if item.readingTime}
          <span class="content-card__reading-time">
            ⏱️ {item.readingTime} {$_('content.readingTime')}
          </span>
        {/if}
      </div>
      
      {#if 'tags' in item && item.tags.length > 0}
        <div class="content-card__tags">
          {#each item.tags.slice(0, 3) as tag}
            <span class="content-card__tag">#{tag}</span>
          {/each}
          {#if item.tags.length > 3}
            <span class="content-card__tag content-card__tag--more">
              +{item.tags.length - 3}
            </span>
          {/if}
        </div>
      {/if}
    </div>
  </div>
</article>
{/if}

<style>
  .content-card {
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    transition: var(--transition-all);
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .content-card:hover {
    border-color: var(--border-medium);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
  
  .content-card--compact {
    padding: var(--space-4);
  }
  
  .content-card__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--space-4);
    gap: var(--space-2);
  }
  
  .content-card__meta {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  
  .content-card__type {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .content-card__icon {
    font-size: var(--font-size-sm);
  }
  
  .content-card__date {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
  }
  
  .content-card__featured {
    font-size: var(--font-size-xs);
    color: var(--brand-primary);
    font-weight: var(--font-weight-medium);
    background: var(--brand-primary-light);
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    white-space: nowrap;
  }
  
  .content-card__content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  .content-card__title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    line-height: 1.3;
    margin-bottom: var(--space-3);
  }
  
  .content-card--compact .content-card__title {
    font-size: var(--font-size-base);
  }
  
  .content-card__link {
    color: var(--text-primary);
    text-decoration: none;
    transition: var(--transition-colors);
  }
  
  .content-card__link:hover {
    color: var(--brand-primary);
  }
  
  .content-card__link--external {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
  }
  
  .external-icon {
    width: 16px;
    height: 16px;
    opacity: 0.6;
  }
  
  .content-card__excerpt {
    color: var(--text-secondary);
    line-height: 1.5;
    margin-bottom: var(--space-4);
    flex: 1;
  }
  
  .content-card--compact .content-card__excerpt {
    font-size: var(--font-size-sm);
    margin-bottom: var(--space-3);
  }
  
  .content-card__footer {
    margin-top: auto;
  }
  
  .content-card__details {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
    margin-bottom: var(--space-3);
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
  }
  
  .content-card__details > span {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }
  
  .content-card__tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
  }
  
  .content-card__tag {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
    background: var(--bg-secondary);
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-light);
  }
  
  .content-card__tag--more {
    color: var(--brand-primary);
    background: var(--brand-primary-light);
    border-color: var(--brand-primary);
  }
  
  @media (max-width: 768px) {
    .content-card {
      padding: var(--space-4);
    }
    
    .content-card__header {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .content-card__details {
      flex-direction: column;
      gap: var(--space-1);
    }
  }
</style>
